import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { signOut, type AuthSession } from "../auth";
import { MapSpritesGlobal } from "./MapSprites";
import { useCurrentGarden } from "../lib/useCurrentGarden";
import "./Layout.css";

interface LayoutProps {
  session: AuthSession;
}

export function Layout({ session }: LayoutProps) {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const email = session.user.email ?? "anonymous";

  const onLogout = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/login", { replace: true });
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="Layout">
      <nav className="Layout__rail" aria-label="Primary">
        <div className="Layout__brand">G</div>
        <NavRailLink to="/map" label="Map">
          <MapIcon />
        </NavRailLink>
        <NavRailLink to="/today" label="Today">
          <CameraIcon />
        </NavRailLink>
        <NavRailLink to="/plants" label="Plants">
          <PlantsIcon />
        </NavRailLink>
        <NavRailLink to="/tasks" label="Tasks">
          <TasksIcon />
        </NavRailLink>
        <NavRailLink to="/observations" label="Observations">
          <ObservationsIcon />
        </NavRailLink>
        <div className="Layout__rail-spacer" />
        <NavRailLink to="/health" label="Health" disabled>
          <HealthIcon />
        </NavRailLink>
      </nav>

      <header className="Layout__topbar">
        <GardenSwitcher />
        <div className="Layout__email" title={email}>
          {email}
        </div>
        <button
          type="button"
          className="Layout__signout"
          onClick={onLogout}
          disabled={signingOut}
        >
          {signingOut ? "Signing out…" : "Sign out"}
        </button>
      </header>

      <main className="Layout__main">
        <Outlet />
      </main>

      <nav className="Layout__tabbar" aria-label="Mobile primary">
        <TabbarLink to="/map" label="Map">
          <MapIcon />
        </TabbarLink>
        <TabbarLink to="/today" label="Today">
          <CameraIcon />
        </TabbarLink>
        <TabbarLink to="/plants" label="Plants">
          <PlantsIcon />
        </TabbarLink>
        <TabbarLink to="/more" label="More">
          <MoreIcon />
        </TabbarLink>
      </nav>

      <MapSpritesGlobal />
    </div>
  );
}

interface NavRailLinkProps {
  to: string;
  label: string;
  disabled?: boolean;
  children: React.ReactNode;
}

function NavRailLink({ to, label, disabled, children }: NavRailLinkProps) {
  if (disabled) {
    return (
      <span
        className="Layout__rail-btn Layout__rail-btn--disabled"
        title={`${label} (coming later)`}
        aria-disabled="true"
      >
        {children}
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        isActive ? "Layout__rail-btn Layout__rail-btn--active" : "Layout__rail-btn"
      }
      title={label}
    >
      {children}
    </NavLink>
  );
}

interface TabbarLinkProps {
  to: string;
  label: string;
  children: React.ReactNode;
}

function TabbarLink({ to, label, children }: TabbarLinkProps) {
  return (
    <NavLink
      to={to}
      end={to === "/more" ? false : false}
      className={({ isActive }) =>
        isActive ? "Layout__tab Layout__tab--active" : "Layout__tab"
      }
    >
      <span className="Layout__tab-icon">{children}</span>
      <span className="Layout__tab-label">{label}</span>
    </NavLink>
  );
}

function MapIcon() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 6l5-2 6 2 5-2v14l-5 2-6-2-5 2V6zm5-2v14m6 0V6" />
    </svg>
  );
}
function CameraIcon() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="11" cy="12" r="4.5" />
      <path d="M3 8h2.5l1.5-2.5h7L15 8h4v10H3V8z" />
    </svg>
  );
}
function PlantsIcon() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M11 19V8m0 0c0-3 2-5 5-5 0 3-2 5-5 5zm0 0c0-3-2-5-5-5 0 3 2 5 5 5z" />
    </svg>
  );
}
function TasksIcon() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="4" width="14" height="14" rx="2" />
      <path d="M8 11l2 2 4-4" />
    </svg>
  );
}
function ObservationsIcon() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M5 5h12v14H5z" />
      <path d="M8 9h6m-6 3h6m-6 3h4" />
    </svg>
  );
}
function HealthIcon() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 12h4l2-5 4 10 2-5h4" />
    </svg>
  );
}
function MoreIcon() {
  return (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="6" cy="11" r="1.5" />
      <circle cx="11" cy="11" r="1.5" />
      <circle cx="16" cy="11" r="1.5" />
    </svg>
  );
}

/** Topbar dropdown to swap the active garden. Hidden when the user is in
 *  zero or one garden — no dead UI. The element-tag is a native `<select>`
 *  to keep keyboard / a11y free; styling lives in Layout.css. */
function GardenSwitcher() {
  const { gardens, current, setCurrentGardenId } = useCurrentGarden();
  if (gardens.length < 2 || !current) return null;
  return (
    <select
      className="Layout__gardenSwitcher"
      value={current.id}
      onChange={(e) => setCurrentGardenId(e.target.value)}
      aria-label="Switch garden"
    >
      {gardens.map((g) => (
        <option key={g.id} value={g.id}>
          {g.name}
        </option>
      ))}
    </select>
  );
}
